import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  LinearProgress,
  Fade,
  Tooltip,
} from '@mui/material';
import {
  MoreVertical,
  Edit,
  Eye,
  Download,
  Delete,
  Calendar,
  Building,
  MapPin,
  DollarSign,
  RefreshCw,
  Database,
  Copy,
} from 'lucide-react';
import { useNotification } from './Notification';

const CertificateCard = ({
  certificate,
  onView,
  onEdit,
  onDelete,
  onDownload,
  onConvertToProduction, // Function to handle draft conversion
  onSyncCOCFile, // Function to handle COC file sync
  onReissue, // Function to handle certificate reissue
  isDarkMode,
  isOffice,
  isAdmin,
  sx, // Accept styling props from parent
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncingCOC, setIsSyncingCOC] = useState(false);
  const { showNotification } = useNotification();

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      await onDownload(certificate);
      showNotification('Certificate downloaded successfully', 'success');
    } catch (error) {
      showNotification('Failed to download certificate', 'error');
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      setIsLoading(true);
      try {
        await onDelete(certificate);
        showNotification('Certificate deleted successfully', 'success');
      } catch (error) {
        showNotification('Failed to delete certificate', 'error');
      } finally {
        setIsLoading(false);
        handleMenuClose();
      }
    }
  };

  const handleConvertToProduction = () => {
    onConvertToProduction(certificate);
    handleMenuClose();
  };

  const handleSyncCOCFile = async () => {
    setIsSyncingCOC(true);
    try {
      await onSyncCOCFile(certificate);
      showNotification('COC file synced successfully', 'success');
    } catch (error) {
      showNotification('Failed to sync COC file', 'error');
    } finally {
      setIsSyncingCOC(false);
      handleMenuClose();
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // FIXED: Utility function to truncate long text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    // Remove line breaks and extra spaces
    const cleanText = text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    return cleanText.length > maxLength
      ? cleanText.substring(0, maxLength) + '...'
      : cleanText;
  };

  // Enhanced status logic with draft consideration
  const isDraft = certificate.IsDraft;
  const isExpired =
    !isDraft &&
    certificate.ExpiryDate &&
    new Date(certificate.ExpiryDate) < new Date();
  
  // Check if certificate is completed (can be reissued)
  const isCompleted = !isDraft && 
    certificate.IsStage1Sync && 
    certificate.IsStage2Sync;
  
  // Check if this is a reissued certificate
  const isReCertificate = certificate.IsReCertificate === true;

  // Define card styling based on status
  const getCardStyling = () => {
    if (isDraft) {
      return {
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(251, 146, 60, 0.08) 0%, rgba(249, 115, 22, 0.08) 100%)',
        border: '2px solid rgba(251, 146, 60, 0.3)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(251, 146, 60, 0.2)'
          : '0 8px 32px rgba(251, 146, 60, 0.15)',
      };
    } else if (isExpired) {
      return {
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)',
        border: '2px solid rgba(239, 68, 68, 0.3)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(239, 68, 68, 0.2)'
          : '0 8px 32px rgba(239, 68, 68, 0.15)',
      };
    } else {
      return {
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
        border: isDarkMode
          ? '1px solid rgba(148, 163, 184, 0.1)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        boxShadow: isDarkMode
          ? '0 8px 32px rgba(0, 0, 0, 0.3)'
          : '0 8px 32px rgba(0, 0, 0, 0.1)',
      };
    }
  };

  const getStatusChip = () => {
    if (isDraft) {
      return {
        label: '📝 DRAFT',
        color: '#f59e0b',
        background: 'linear-gradient(135deg, #f59e0b20, #f59e0b10)',
        border: '1px solid #f59e0b30',
      };
    } else if (isExpired) {
      return {
        label: '⏰ EXPIRED',
        color: '#ef4444',
        background: 'linear-gradient(135deg, #ef444420, #ef444410)',
        border: '1px solid #ef444430',
      };
    } else {
      return {
        label: '✅ ACTIVE',
        color: '#10b981',
        background: 'linear-gradient(135deg, #10b98120, #10b98110)',
        border: '1px solid #10b98130',
      };
    }
  };

  const cardStyling = getCardStyling();
  const statusChip = getStatusChip();

  return (
    <Fade in={true} timeout={300}>
      <Card
        sx={{
          // FIXED: Apply parent styling and override with consistent sizing
          ...sx,
          ...cardStyling,
          minHeight: 250,
          maxHeight: 250,
          maxWidth: 300,
          minWidth: 300,
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDraft
              ? isDarkMode
                ? '0 20px 40px rgba(251, 146, 60, 0.4)'
                : '0 20px 40px rgba(251, 146, 60, 0.3)'
              : isExpired
                ? isDarkMode
                  ? '0 20px 40px rgba(239, 68, 68, 0.4)'
                  : '0 20px 40px rgba(239, 68, 68, 0.3)'
                : isDarkMode
                  ? '0 20px 40px rgba(0, 0, 0, 0.4)'
                  : '0 20px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {/* Status Indicator Bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: isDraft
              ? 'linear-gradient(90deg, #f59e0b, #d97706)'
              : isExpired
                ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                : 'linear-gradient(90deg, #10b981, #059669)',
          }}
        />

        <CardContent
          sx={{
            p: 3,
            pt: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // FIXED: Prevent overflow
          }}
        >
          {/* Header with COC Number, Status and Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {' '}
              {/* FIXED: minWidth 0 for text truncation */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {certificate.CocNumber || `Draft-${certificate.Id}`}
                </Typography>

                {/* Status Chip */}
                <Chip
                  label={statusChip.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: statusChip.color,
                    background: statusChip.background,
                    border: statusChip.border,
                    flexShrink: 0,
                  }}
                />
                
                {/* Reissued Certificate Badge */}
                {isReCertificate && (
                  <Chip
                    label="🔄 REISSUED"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#8b5cf6',
                      background: 'linear-gradient(135deg, #8b5cf620, #8b5cf610)',
                      border: '1px solid #8b5cf630',
                      flexShrink: 0,
                      ml: 0.5,
                    }}
                  />
                )}
              </Box>
              {/* FIXED: Truncated Importer Name with Tooltip */}
              <Tooltip
                title={certificate.ImporterName || 'Unknown Importer'}
                arrow
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {truncateText(
                    certificate.ImporterName || 'Unknown Importer',
                    30
                  )}
                </Typography>
              </Tooltip>
              {/* Draft-specific information */}
              {isDraft && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#f59e0b',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  💡 Ready for production conversion
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                flexShrink: 0,
                '&:hover': {
                  background: isDarkMode
                    ? 'rgba(148, 163, 184, 0.1)'
                    : 'rgba(148, 163, 184, 0.1)',
                },
              }}
              disabled={isLoading || isSyncingCOC}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Box>

          {/* FIXED: Certificate Details with proper truncation */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {certificate.ImporterAddress && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <MapPin
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Tooltip title={certificate.ImporterAddress} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 1,
                      color: isDarkMode ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateText(certificate.ImporterAddress, 35)}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {certificate.ExporterName && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Building
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Tooltip title={certificate.ExporterName} arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 1,
                      color: isDarkMode ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncateText(certificate.ExporterName, 35)}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {certificate.InvoiceValue && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DollarSign
                  size={14}
                  color={isDarkMode ? '#94a3b8' : '#64748b'}
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 1,
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCurrency(
                    certificate.InvoiceValue,
                    certificate.InvoiceCurrency
                  )}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, opacity: 0.3 }} />

            {/* FIXED: Footer section with flexShrink 0 to prevent squashing */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                mt: 'auto',
              }}
            >
              {/* Date information - different for drafts vs production */}
              {!isDraft ? (
                <>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
                  >
                    <Calendar
                      size={12}
                      color={isDarkMode ? '#64748b' : '#94a3b8'}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 0.5,
                        color: isDarkMode ? '#64748b' : '#94a3b8',
                        fontSize: '0.7rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Issued: {formatDate(certificate.IssuanceDate)}
                    </Typography>
                  </Box>

                  {certificate.ExpiryDate && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isExpired
                          ? '#ef4444'
                          : isDarkMode
                            ? '#64748b'
                            : '#94a3b8',
                        fontSize: '0.7rem',
                        flexShrink: 0,
                      }}
                    >
                      Expires: {formatDate(certificate.ExpiryDate)}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
                  >
                    <Calendar
                      size={12}
                      color="#f59e0b"
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 0.5,
                        color: '#f59e0b',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Created: {formatDate(certificate.CreatedAt)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#f59e0b',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    🚀 Convert to production
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {isLoading && (
            <LinearProgress
              sx={{
                mt: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                flexShrink: 0,
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                },
              }}
            />
          )}
        </CardContent>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              borderRadius: 2,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              onView(certificate);
              handleMenuClose();
            }}
          >
            <Eye size={16} style={{ marginRight: 8 }} />
            View Details
          </MenuItem>

          {/* Convert to Production option for drafts */}
          {isDraft && isOffice && (
            <MenuItem
              onClick={handleConvertToProduction}
              sx={{
                color: '#10b981',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                },
              }}
            >
              <RefreshCw size={16} style={{ marginRight: 8 }} />
              Convert to Production
            </MenuItem>
          )}

          {(isAdmin || isOffice) && (
            <MenuItem
              onClick={() => {
                onEdit(certificate);
                handleMenuClose();
              }}
            >
              <Edit size={16} style={{ marginRight: 8 }} />
              Edit Certificate
            </MenuItem>
          )}

          <MenuItem onClick={handleDownload}>
            <Download size={16} style={{ marginRight: 8 }} />
            Download PDF {isDraft && '(Draft)'}
          </MenuItem>

          {/* Reissue Certificate option for completed certificates */}
          {isCompleted && (isAdmin || isOffice) && onReissue && (
            <MenuItem
              onClick={() => {
                onReissue(certificate);
                handleMenuClose();
              }}
              sx={{
                color: '#8b5cf6',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              <Copy size={16} style={{ marginRight: 8 }} />
              Reissue Certificate
            </MenuItem>
          )}

          {/* Sync COC File option for fully synced production certificates */}
          {!isDraft && 
           certificate.IsStage1Sync && 
           certificate.IsStage2Sync && 
           certificate.CocNumber && 
           (isAdmin || isOffice) && (
            <MenuItem 
              onClick={handleSyncCOCFile}
              disabled={isSyncingCOC}
              sx={{
                color: '#f59e0b',
                '&:hover': {
                  backgroundColor: 'rgba(245, 158, 11, 0.08)',
                },
              }}
            >
              {isSyncingCOC ? (
                <RefreshCw size={16} className="animate-spin" style={{ marginRight: 8 }} />
              ) : (
                <Database size={16} style={{ marginRight: 8 }} />
              )}
              {isSyncingCOC ? 'Syncing...' : 'Sync COC File'}
            </MenuItem>
          )}

          {isAdmin && (
            <MenuItem onClick={handleDelete} sx={{ color: '#ef4444' }}>
              <Delete size={16} style={{ marginRight: 8 }} />
              Delete
            </MenuItem>
          )}
        </Menu>
      </Card>
    </Fade>
  );
};

export default CertificateCard;
